<template>
    <div>
        <v-dialog
            v-model="display"
            persistent
            width="750"
            scrollable>
            <v-card v-if="loading.data">
                <v-skeleton-loader type="card"></v-skeleton-loader>
            </v-card>
            <v-card
                v-if="!loading.data">
                <v-card-title>
                    {{ title }}
                    <v-spacer></v-spacer>
                    <v-btn
                        icon
                        @click="fetchAllLookups({cache: false})">
                        <v-icon>mdi-refresh</v-icon>
                    </v-btn>
                </v-card-title>
                <v-card-subtitle>{{ $t('TRUCK_SUBTITLE') }}</v-card-subtitle>
                <v-card-text>
                    <div class="text-center" v-if="loading.data">
                        <v-progress-circular
                            indeterminate
                            color="primary">
                        </v-progress-circular>
                    </div>
                    <v-form
                        ref="form"
                        v-model="valid"
                        v-if="!loading.data">
                        <v-row>
                            <v-col>
                                <v-text-field
                                    :label="$t('TRUCK_PLATE')"
                                    v-model="form.plate_number"
                                    :rules="rules.plate_number"
                                    >
                                </v-text-field>
                            </v-col>
                            <v-col>
                                <v-autocomplete
                                    :label="$t('TRUCK_TYPE')"
                                    :items="truckTypes"
                                    item-text="name"
                                    item-value="id"
                                    v-model="form.truck_type_id"
                                    :rules="rules.truck_type_id"
                                    :loading="loading.truckTypes"
                                    :disabled="loading.truckTypes"
                                    >
                                    <template v-slot:append-item v-if="authorizations.truck_types.create">
                                        <v-divider></v-divider>
                                        <v-list>
                                            <v-list-item link @click="openTruckTypeDialog">
                                                <v-list-item-icon>
                                                    <v-icon>mdi-database-plus</v-icon>
                                                </v-list-item-icon>
                                                <v-list-item-content>
                                                    <v-list-item-title>{{ $t('TRUCK_TYPE_NEW_TITLE') }}</v-list-item-title>
                                                </v-list-item-content>
                                            </v-list-item>
                                        </v-list>
                                    </template>
                                </v-autocomplete>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col>        
                                <v-autocomplete
                                    :label="$t('TRUCK_BRAND')"
                                    :items="truckBrands"
                                    item-text="name"
                                    item-value="id"
                                    :loading="loading.truckBrands"
                                    :disabled="loading.truckBrands"
                                    v-model="form.truck_brand_id"
                                    :rules="rules.truck_brand_id"
                                    @change="fetchTruckModels({truck_brand_id: form.truck_brand_id})"
                                    >
                                    <template v-slot:append-item v-if="authorizations.truck_brands.create">
                                        <v-divider></v-divider>
                                        <v-list>
                                            <v-list-item link @click="openTruckBrandDialog">
                                                <v-list-item-icon>
                                                    <v-icon>mdi-database-plus</v-icon>
                                                </v-list-item-icon>
                                                <v-list-item-content>
                                                    <v-list-item-title>
                                                        {{ $t('TRUCK_BRAND_NEW_TITLE') }}
                                                    </v-list-item-title>
                                                </v-list-item-content>
                                            </v-list-item>
                                        </v-list>
                                    </template>
                                </v-autocomplete>
                            </v-col>
                            <v-col>
                                <v-select
                                    :label="$t('MODEL_NAME')"
                                    v-model="form.truck_model_id"
                                    clearable
                                    v-if="!mode.mini"
                                    :items="truckModels"
                                    item-text="name"
                                    item-value="id">
                                </v-select>
                            </v-col>
                            <v-col>
                                <v-text-field
                                    :label="$t('TRUCK_YEAR_MAKE')"
                                    v-model="form.year_make"
                                    :rules="rules.year_make"
                                    v-if="!mode.mini">
                                </v-text-field>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col>
                                <v-text-field
                                    :label="$t('TRUCK_CHASIS')"
                                    v-model="form.chasis_number"
                                    v-if="!mode.mini">
                                </v-text-field>
                            </v-col>
                            <v-col>
                                <v-text-field
                                    :label="$t('TRUCK_ENGN_NUM')"
                                    v-model="form.engine_number"
                                    v-if="!mode.mini">
                                </v-text-field>
                            </v-col>
                        </v-row>
                        <v-switch
                            :label="$t('track_distance')"
                            v-model="form.track_distance">
                        </v-switch>
                        <v-textarea
                            :label="$t('TRUCK_NOTES')"
                            v-model="form.notes"
                            v-if="!mode.mini">
                        </v-textarea>
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-btn
                        :disabled="!valid || loading.submit || loading.data"
                        :loading="loading.submit"
                        text
                        @click="submit"
                        >
                        {{ $t('BTN_SAVE') }}
                        </v-btn>
                    <v-btn
                        text
                        @click="close"
                        >
                        {{ $t('BTN_CANCEL') }}
                        </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <TruckTypeDialog 
            ref="truckTypeDialog"
            @record="selectNewTruckType"
            />
        <AlertDialog 
            ref="alertDialog"/>
        <TruckBrandDialog
            ref="truckBrandDialog"
            @record="selectNewTruckBrand"/>
    </div>
</template>

<script>
import TruckTypeDialog from '../components/TruckTypeDialog';
import TruckBrandDialog from '../components/TruckBrandDialog';
import AlertDialog from '../components/AlertDialog';
import { mapActions, mapState } from 'vuex';
import fetchLookups from '../utils/fetchLookups';
export default {
    name: "TruckDialog",
    props: {
        miniMode: Boolean
    },
    components: {
        TruckTypeDialog,
        TruckBrandDialog,
        AlertDialog
    },
    data() {
        return {
            display: false,
            valid: false,
            editMode: false,
            id: null,
            form: {
                plate_number: null,
                truck_type_id: null,
                truck_brand_id: null,
                truck_model_id: null,
                year_make: null,
                chasis_number: null,
                engine_number: null,
                notes: null,
                track_distance: false
            },
            rules: {
                plate_number: [
                    v => !!v || this.$t('RULES_REQUIRED_MSG')
                ],
                truck_type_id: [
                    v => !!v || this.$t('RULES_REQUIRED_MSG')
                ],
                truck_brand_id: [
                    v => !!v || this.$t('RULES_REQUIRED_MSG')
                ],
                year_make: [
                    v => (v.length ? /(\d{4})/.test(v) : true) || this.$t('TRUCK_RUL_YEAR_MAKE_FORMAT'),
                    v => (v.length ? v.length === 4 : true) || this.$t('TRUCK_RUL_YEAR_MAKE_FORMAT')
                ]
            },
            loading: {
                data: false,
                submit: false,
                truckTypes: false,
                truckBrands: false,
                form: {
                    truck_model_id: false
                }
            },
            mode: {
                mini: false
            }
        }
    },
    computed: {
        ...mapState("truckType", ["truckTypes"]),
        ...mapState("truckBrand", ["truckBrands"]),
        ...mapState("truck", ["truck"]),
        ...mapState("authentication", ["loggedInUser", "authorizations"]),
        ...mapState("truckModel", ["truckModels"]),
        title() {
            if(!this.editMode) return this.$t("TRUCK_NEW_TITLE");
            else return this.$t("TRUCK_EDIT_TITLE")
        }
    },
    methods: {
        fetchLookups,
        ...mapActions("truckType", ["fetchTruckTypes"]),
        ...mapActions("truckBrand", ["fetchTruckBrands"]),
        ...mapActions("truck", ["fetchTruck", "fetchTrucks", "storeTruck", "updateTruck"]),
        ...mapActions("truckModel", ["fetchTruckModels"]),
        close() {
            this.display = false;
            this.loading.submit = false;
            this.$refs.form.reset();
            this.$refs.form.resetValidation();
        },
        async open(params) {
            this.editMode = params.editMode;
            this.mode.mini = params.miniMode;
            this.display = true;
            this.fetchAllLookups({cached: true});
            if(this.editMode) {
                this.loading.data = true;
                this.id = params.id;
                await this.fetchTruck(this.id);
                this.form = this.truck;
                this.loading.data = false;
                if(this.form.truck_brand_id) this.fetchTruckModels({truck_brand_id: this.form.truck_brand_id})
            }
        },
        fetchAllLookups({cached}) {
            this.fetchLookups([
                {target: "truckTypes", action: this.fetchTruckTypes(), cached},
                {target: "truckBrands", action: this.fetchTruckBrands(), cached},
            ]);
        },
        async onClickFeildLoadingData() {
            await Promise.all([this.fetchTruckTypes(), this.fetchTruckBrands()]);
        },
        openTruckTypeDialog() {
            this.$refs.truckTypeDialog.open({editMode: false});
        },
        openTruckBrandDialog() {
            this.$refs.truckBrandDialog.open({editMode: false});
        },
        selectNewTruckType(record) {
            this.form.truck_type_id = record.id;
        },
        selectNewTruckBrand(record) {
            this.form.truck_brand_id = record.id;
        },
        submit() {
            this.loading.submit = true;
            if(!this.editMode) {
                this.storeTruck(this.form)
                    .then(response => {
                        this.close();
                        this.$refs.alertDialog.open(this.$t('SUCCESS_TITLE'), response.message);
                        this.$emit('record', response.record);
                        this.$emit("submit");
                    })
                    .catch(error => {
                        this.close();
                        this.$refs.alertDialog.open(this.$t('ERROR_TITLE'), error.message);
                    })
            } else {
                this.updateTruck({form: this.form, id: this.id})
                    .then(response => {
                        this.close();
                        this.$refs.alertDialog.open(this.$t('SUCCESS_TITLE'), response.message);
                        this.$emit("submit");
                    })
                    .catch(error => {
                        this.close();
                        this.$refs.alertDialog.opem(this.$t('ERROR_TITLE'), error.message);
                    })
            }
        }
    }
}
</script>